import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  NotificationListAvatar,
  SHELL_HEADER_DROPDOWN_Z_INDEX,
  notificationDisplayTitle,
  notificationDisplayMessage,
} from '../../notifications/notificationUtils';
import './Notifications.css';

const baseUrlFallback = process.env.REACT_APP_BASE_URL || '';
const DROPDOWN_WIDTH = 400;

const formatNotificationDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const NotificationsDropdown = ({
  baseUrl = baseUrlFallback,
  onMarkRead,
  onNavigateToAll,
  profileMode = 'personal',
  anchorRef = null,
  portaled = true,
}) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const dropdownRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/api/notifications/list`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileMode }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data.notifications)) setNotifications(data.notifications);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [baseUrl, profileMode]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const updateDropdownPosition = useCallback(() => {
    if (!portaled) {
      setDropdownStyle(null);
      return;
    }
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 5;
    const maxHeight = Math.max(160, window.innerHeight - rect.bottom - gap - 16);
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      right: Math.max(8, window.innerWidth - rect.right),
      width: DROPDOWN_WIDTH,
      maxHeight,
      zIndex: SHELL_HEADER_DROPDOWN_Z_INDEX,
    });
  }, [anchorRef, portaled]);

  useLayoutEffect(() => {
    if (!portaled) return undefined;
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [portaled, updateDropdownPosition, loading, notifications.length]);

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${baseUrl}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      });
      if (onMarkRead) onMarkRead();
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, notificationStatus: 'read', userSeenDate: new Date().toISOString() } : n
        )
      );
    } catch (e) {
      // ignore
    }
  };

  const handleClearAll = async () => {
    const unreadIds = notifications.filter((n) => n.notificationStatus === 'unread').map((n) => n.notificationId);
    if (!unreadIds.length) return;
    try {
      await fetch(`${baseUrl}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationIds: unreadIds }),
      });
      if (onMarkRead) onMarkRead();
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationStatus === 'unread'
            ? { ...n, notificationStatus: 'read', userSeenDate: new Date().toISOString() }
            : n
        )
      );
    } catch (e) {
      // ignore
    }
  };

  const handleItemClick = (notificationId) => {
    if (onNavigateToAll) onNavigateToAll();
    navigate(`/notifications?selected=${encodeURIComponent(notificationId)}`);
  };

  const handleSeeAll = () => {
    if (onNavigateToAll) onNavigateToAll();
    navigate('/notifications');
  };

  const recent = notifications.slice(0, 8);
  const unreadCount = notifications.filter((n) => n.notificationStatus === 'unread').length;

  const dropdownPanel = (
    <div
      ref={dropdownRef}
      className={`notifications-dropdown${portaled ? ' notifications-dropdown--portal' : ''}`}
      style={portaled ? dropdownStyle || undefined : undefined}
    >
      <div className="notifications-heading-row">
        <h3 className="notifications-heading">
          Notifications
          {unreadCount > 0 && <span className="notifications-heading-unread-dot" aria-hidden />}
        </h3>
        {notifications.length > 0 && (
          <button
            type="button"
            className="notifications-clear-btn"
            onClick={handleClearAll}
          >
            Clear
          </button>
        )}
      </div>
      {loading ? (
        <ul className="notifications-skeleton-list">
          {[1, 2, 3].map((i) => (
            <li key={i} className="notifications-item notifications-item-skeleton">
              <span className="notifications-item-icon skeleton-circle" />
              <span className="notifications-item-body">
                <span className="skeleton-line skeleton-line-short" />
                <span className="skeleton-line skeleton-line-long" />
              </span>
            </li>
          ))}
        </ul>
      ) : recent.length === 0 ? (
        <p className="notifications-empty">No notifications</p>
      ) : (
        <ul>
          {recent.map((n) => {
            const title = notificationDisplayTitle(n);
            const message = notificationDisplayMessage(n);
            return (
            <li
              key={n.notificationId}
              className={`notifications-item ${n.notificationStatus === 'unread' ? 'notifications-item-unread' : ''}`}
              onClick={() => handleItemClick(n.notificationId)}
            >
              <span className="notifications-item-icon" title={n.postContentType || n.kind || 'Notification'}>
                <NotificationListAvatar notification={n} size={18} profileMode={profileMode} baseUrl={baseUrl} />
              </span>
              <span className="notifications-item-body">
                <span className="notifications-item-title">{title}</span>
                {message ? (
                  <span className="notifications-item-message">
                    {message.length > 50 ? `${message.slice(0, 50)}...` : message}
                  </span>
                ) : null}
                <span className="notifications-item-time">{formatNotificationDate(n.notificationDate)}</span>
              </span>
              {n.notificationStatus === 'unread' && (
                <button
                  type="button"
                  className="notifications-mark-read-btn"
                  onClick={(e) => { e.stopPropagation(); handleMarkRead(n.notificationId); }}
                  title="Mark as read"
                >
                  Mark read
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}
      <div className="all-notifications" onClick={handleSeeAll}>
        See all notifications
      </div>
    </div>
  );

  if (portaled) {
    return dropdownStyle ? createPortal(dropdownPanel, document.body) : null;
  }

  return <div className="notifications-container">{dropdownPanel}</div>;
};

export default NotificationsDropdown;
